import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPasswordUser } from '../../store/reducers/authReducer';
import { useI18n } from '../../hooks/useI18n';
import SEO from '../../components/seo/SEO';

const AuthWrap = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
`;

const AuthCard = styled(motion.div)`
  width: 100%;
  max-width: 26rem;
  padding: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const LogoLink = styled(Link)`
  display: inline-block;
  font-size: 1.6rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 1.5rem 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  box-sizing: border-box;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitBtn = styled.button`
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 1.25rem 0 0 0;
  text-align: center;
`;

const AuthLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  display: block;
  text-align: center;
  margin-top: 0.5rem;
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMsg = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

export default function ForgotPasswordPage() {
  const location = useLocation();
  const region = regionFromPathname(location.pathname);
  const dispatch = useDispatch();
  const { forgotMessage, forgotDebugToken, error } = useSelector((s) => s.auth);
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(forgotPasswordUser({ email }))
      .unwrap()
      .then(() => setSubmitted(true))
      .catch(() => setSubmitted(false));
  };

  return (
    <PageLayout>
      <SEO
        region={region}
        path="/forgot-password"
        title={t('auth.forgot.title', 'Forgot password?')}
        description={t('auth.forgot.subtitle', 'Enter your email and we will send reset instructions.')}
        noindex
      />
      <AuthWrap>
        <Container style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <AuthCard initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <BackLink to={pathWithRegion('/login', region)}>
              <ArrowLeft size={16} strokeWidth={2.5} /> Back
            </BackLink>
            <LogoLink to={pathWithRegion('/', region)}>FliqMatch</LogoLink>

            <Title>{t('auth.forgot.title', 'Forgot password?')}</Title>
            <Subtitle>{t('auth.forgot.subtitle', "Enter your email and we'll send reset instructions.")}</Subtitle>

            {submitted ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CheckCircle size={40} style={{ color: 'hsl(142, 71%, 45%)' }} />
                </div>
                <p style={{ margin: '1rem 0 0 0', fontWeight: 600, textAlign: 'center' }}>Check your email</p>
                <SuccessMsg style={{ textAlign: 'center' }}>
                  {forgotMessage || `If an account exists for ${email}, you'll receive a reset link shortly.`}
                </SuccessMsg>
                {forgotDebugToken ? (
                  <SuccessMsg style={{ textAlign: 'center' }}>
                    Debug reset token: <code>{forgotDebugToken}</code>
                  </SuccessMsg>
                ) : null}
                {forgotDebugToken ? (
                  <SuccessMsg style={{ textAlign: 'center' }}>
                    <Link to={`${pathWithRegion('/reset-password', region)}?token=${encodeURIComponent(forgotDebugToken)}`}>
                      Open reset page with token
                    </Link>
                  </SuccessMsg>
                ) : null}
                <Divider style={{ marginTop: '1.5rem' }}>
                  <AuthLink to={pathWithRegion('/login', region)}>Return to log in</AuthLink>
                </Divider>
              </>
            ) : (
              <>
                <Form onSubmit={handleSubmit}>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <SubmitBtn type="submit">
                    <Mail size={18} /> Send reset link
                  </SubmitBtn>
                  {error ? <SuccessMsg style={{ color: '#ffb4b4' }}>{error}</SuccessMsg> : null}
                </Form>
                <Divider>Remember your password?</Divider>
                <AuthLink to={pathWithRegion('/login', region)}>Log in</AuthLink>
              </>
            )}
          </AuthCard>
        </Container>
      </AuthWrap>
    </PageLayout>
  );
}

